import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, CloudLightning, Copy } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { HeaderDelay } from '@/components/delay/header-delay';
import { EscalationAlert } from '@/components/delay-escalation/escalation-alert';
import { OutOfPolicyAlert } from '@/components/delay-out-of-policy/out-of-policy-alert';
import { cn } from '@/lib/cn';
import { openerLine, openerOf } from '@/lib/disruptions/opener';
import type { DisruptionDetail } from '@/lib/disruptions/types';
import { DisruptionViewSwitcher } from './view-switcher';
import {
  airportCodes,
  causeLabel,
  formatPriceMinor,
  formatDateTime,
  STALENESS_TONE,
  statusLabel,
  STATUS_TONE,
  stalenessChip,
} from './labels';

// Descriptive disruption header (NEX-299 item 2) — mirrors the case-detail
// CaseHeader pattern (routes/case-detail/case-header.tsx): a pinned header block
// that describes the entity at a glance. Friendly title primary, URN secondary +
// copyable (frontend-conventions §18.3). Carries status/cause badges, the
// scope/window meta line and the key rollup counts — so the operator has the full
// picture without scrolling into the Overview tab. Containment is NOT an action
// here: it follows the Overview's step-6 rule (`disruption.tds.md` §3.1 revised).

interface DisruptionHeaderProps {
  detail: DisruptionDetail | undefined;
  isLoading: boolean;
  isError: boolean;
}

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="flex min-w-0 flex-col">
      <span className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</span>
      <span className="truncate text-sm font-semibold tabular-nums">{value}</span>
    </div>
  );
}

export function DisruptionHeader({ detail, isLoading, isError }: DisruptionHeaderProps) {
  const { t } = useTranslation('disruptions');
  const [copied, setCopied] = useState(false);

  const copy = async (): Promise<void> => {
    if (!detail) return;
    try {
      await navigator.clipboard?.writeText(detail.urn);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable — no-op */
    }
  };

  if (isLoading || !detail) {
    return (
      <header className="border-b border-border bg-background px-4 py-3 sm:px-6">
        <Skeleton className="h-6 w-72" />
        <Skeleton className="mt-2 h-3 w-56" />
      </header>
    );
  }

  const airports = airportCodes(detail.scope.airports);
  const window = [detail.scope.window?.fromUtc, detail.scope.window?.toUtc].some(Boolean)
    ? `${formatDateTime(detail.scope.window?.fromUtc)} → ${formatDateTime(detail.scope.window?.toUtc)}`
    : null;

  return (
    <header
      className="flex flex-wrap items-start justify-between gap-x-6 gap-y-3 border-b border-border bg-background px-4 py-3 sm:px-6"
      data-testid="disruption-header"
    >
      <div className="min-w-0 max-w-full">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          <h1 className="flex items-center gap-2 text-base font-semibold sm:text-lg">
            <CloudLightning className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
            <span className="truncate">{detail.title}</span>
          </h1>
          <span
            className={cn('rounded px-2 py-0.5 text-xs font-medium', STATUS_TONE[detail.status])}
            data-testid="disruption-status-badge"
          >
            {statusLabel(detail.status, t)}
          </span>
          <span className="rounded bg-muted px-2 py-0.5 text-xs uppercase text-muted-foreground">
            {causeLabel(detail.cause, t)}
          </span>
          {detail.staleness && detail.staleness.state !== 'OK' ? (
            <span
              className={cn(
                'rounded px-2 py-0.5 text-xs font-semibold',
                STALENESS_TONE[detail.staleness.severity],
              )}
              title={detail.staleness.reason}
              data-testid="disruption-staleness-badge"
            >
              ⚠ {stalenessChip(detail.staleness, t)}
            </span>
          ) : null}
          {/* Flight delay (flight-delay U1): STD → ETD +X, "rebooking on hold", and the
              delay dialog (timeline + declare / update the ETD). Wraps on a phone. */}
          <HeaderDelay detail={detail} />
          {isError ? (
            <span className="text-xs text-destructive">{t('header.detailsUnavailable')}</span>
          ) : null}
        </div>

        {/* Delay → cancellation (flight-delay U5, M2): the airline's grace window with a
            live countdown, then the outcome (reassigned / sent to Nexa rebooking /
            conflicts). Nothing escalated ⇒ renders nothing. Wraps on a phone. */}
        <EscalationAlert detail={detail} />

        {/* A provider delay over the policy maximum (delay-limits U2): recorded, not
            applied — «Declarar cancelado» / «Descartar». Nothing flagged ⇒ nothing. */}
        <OutOfPolicyAlert detail={detail} />

        {/* SLA breach line — the reconciler's reason + recommended action, so the
            operator knows WHY it's flagged and what to do (disruption.tds §3.3). */}
        {detail.staleness && detail.staleness.state !== 'OK' ? (
          <p className="mt-1 text-xs font-medium text-amber-700 dark:text-amber-300">
            {detail.staleness.reason}
            {detail.staleness.recommendedAction !== 'NONE'
              ? ` ${t('header.staleness.recommended', {
                  action: t(`header.staleness.action.${detail.staleness.recommendedAction}`, {
                    defaultValue: detail.staleness.recommendedAction.toLowerCase(),
                  }),
                })}`
              : ''}
          </p>
        ) : null}

        {/* Scope + window meta line — airports first, then the disruption window. */}
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
          <span>
            <span className="uppercase tracking-wide">{t('header.meta.airports')}</span>{' '}
            {airports || '—'}
          </span>
          {window ? (
            <span>
              <span className="uppercase tracking-wide">{t('header.meta.window')}</span> {window}
            </span>
          ) : null}
          <span>
            <span className="uppercase tracking-wide">{t('header.meta.detected')}</span>{' '}
            {formatDateTime(detail.detectedAt)}
          </span>
          {/* Who declared it — a person, Nexa on a signal, or "not recorded" (openedBy@v1). */}
          <span data-testid="disruption-opener">{openerLine(openerOf(detail), t)}</span>
        </div>

        {/* URN — secondary, muted, copyable (never the title). */}
        <button
          type="button"
          onClick={() => void copy()}
          title={t('header.copyUrn')}
          className="mt-1 inline-flex max-w-full items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground"
        >
          <span className="truncate font-mono">{detail.urn}</span>
          {copied ? (
            <Check className="h-3 w-3 shrink-0 text-emerald-500" aria-hidden />
          ) : (
            <Copy className="h-3 w-3 shrink-0" aria-hidden />
          )}
          <span className="sr-only">{t('header.copyUrn')}</span>
        </button>
      </div>

      {/* Key rollup counts + the primary action, right-aligned. */}
      <div className="flex items-center gap-4">
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <Stat label={t('header.stats.cases')} value={detail.rollup.caseCount} />
          <Stat label={t('header.stats.affected')} value={detail.rollup.affectedPassengers} />
          <Stat label={t('header.stats.rebooked')} value={detail.rollup.rebookedPassengers} />
          <Stat label={t('header.stats.hotels')} value={detail.rollup.hotelsIssued} />
          {/* airline-money@v2 — the airline's PRICE (converted server-side); null →
              «No disponible», never a 0; no amount at all (a list-shaped rollup) → "—". */}
          <Stat
            label={
              detail.rollup.priceSource === 'actual+estimate'
                ? t('header.stats.price')
                : t('header.stats.estPrice')
            }
            value={
              detail.rollup.totalPriceMinor === undefined
                ? '—'
                : formatPriceMinor(detail.rollup.totalPriceMinor, detail.rollup.currency, t)
            }
          />
        </div>
        {/* No "Mark contained" here: containment follows the rule in the Overview's
            step 6 (natural when everyone travelled/declined; forced by a supervisor
            only while nothing is irreversible, releasing holds + selections). */}
        <DisruptionViewSwitcher urn={detail.urn} current="classic" />
      </div>
    </header>
  );
}
